/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import Axios from 'axios';
import { reject } from 'q';
import AppConstants from './appConstants';
import localStorageHelper from './helpers/local-storage-helper';
import messagesNotifyHelper from './helpers/messages-notify-helper';
import messageDialogHelper from './helpers/message-dialog-helper';
import browserInfoHelper from './helpers/browser-info-helper';
import authService from './providers/authService';
import { EventBus } from './event-bus';
import VueI18n from './i18n/i18n';
import store from './store/store';
import checkDeviceTypeHelper from './helpers/check-device-type-helper';
import menuBuilderHelper from './helpers/menu-builder-helper';

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Routes
        // =============================================================================
        {
          path: '/',
          redirect: `${menuBuilderHelper.getInformativePageFirstItemMenu()}`
        },
        {
          path: '/home',
          name: 'home',
          component: () => import('./views/Home.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: '/informative/:commodity',
          name: 'informative',
          component: () => import('./views/pages/informative/Informative.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: '/informative/:commodity/:id',
          name: 'informative-id',
          component: () => import('./views/pages/informative/Informative.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_CHANGE_PASSWORD,
          name: 'change-password',
          component: () => import('./views/pages/ChangePassword.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_ASK_TO_ANALYST,
          name: 'ask-to-analyst',
          component: () => import('./views/pages/analyst/AskTheAnalyst.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_MY_ACCOUNT,
          name: 'my-account',
          component: () => import('./views/pages/MyAccount.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_TMNQUOTES,
          name: 'quotes',
          component: () => import('./views/pages/quotes/TMNQuotes.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        // ACCOUNT
        {
          path: AppConstants.NAVIGATION_PAGE_ACCOUNT,
          name: 'account',
          redirect: AppConstants.NAVIGATION_PAGE_ACCOUNT_NOTIFICATION,
          component: () => import('./views/pages/Account.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          },
        },
        {
          path: `${AppConstants.NAVIGATION_PAGE_ACCOUNT}/:tab`,
          name: 'account-param',
          component: () => import('./views/pages/Account.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          },
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        {
          path: '/',
          redirect: '/page-login'
        },
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: AppConstants.NAVIGATION_PAGE_LOGIN,
          name: 'page-login',
          component: () => import('@/views/pages/Login.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_S4LOGIN,
          name: 's4login',
          component: () => import('./views/pages/S4Login.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_FORGOT_PASSWORD,
          name: 'page-forgot-password',
          component: () => import('@/views/pages/ForgotPassword.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_RESET_PASSWORD,
          name: 'page-reset-password',
          component: () => import('@/views/pages/ResetPassword.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_LOST_ACCESS,
          name: 'page-lost-access',
          component: () => import('@/views/pages/LostAccess.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_REGISTER,
          name: 'page-register',
          component: () => import('@/views/pages/Register.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_PLANS,
          name: 'page-plans',
          component: () => import('@/views/pages/Packages.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_NEW_CLIENT,
          name: 'page-new-client',
          component: () => import('@/views/pages/NewClient.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_ERROR_404,
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_BROWSER_NOT_SUPPORT,
          name: 'browser-not-support',
          component: () => import('@/views/pages/BrowserNotSupport.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_DOWNLOAD_PUBLIC_FILE,
          name: 'download-public-file',
          component: () => import('@/views/pages/DownloadPublicFile.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        {
          path: AppConstants.NAVIGATION_PAGE_MAINTENANCE,
          name: 'page-maintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
          meta: {
            rule: AppConstants.USER_ROLE_PUBLISHER
          }
        },
        // {
        //   path: AppConstants.NAVIGATION_PAGE_LANDING_PAGE,
        //   name: 'landing-page',
        //   component: () => import('@/views/pages/landing-page/LandingPage.vue'),
        //   meta: {
        //     rule: AppConstants.USER_ROLE_PUBLISHER
        //   },
        //   children: [
        //     {
        //       path: '/',
        //       name: 'lading-page-content',
        //       component: () => import('@/views/pages/landing-page/LandingPageContent.vue'),
        //       meta: {
        //         rule: AppConstants.USER_ROLE_PUBLISHER
        //       }
        //     },
        //     {
        //       path: 'details',
        //       name: 'lading-page-details',
        //       component: () => import('@/views/pages/landing-page/LandingPageDetails.vue'),
        //       meta: {
        //         rule: AppConstants.USER_ROLE_PUBLISHER
        //       }
        //     },
        //     {
        //       path: 'details/:title',
        //       name: 'lading-page-details',
        //       component: () => import('@/views/pages/landing-page/LandingPageDetails.vue'),
        //       meta: {
        //         rule: AppConstants.USER_ROLE_PUBLISHER
        //       }
        //     }
        //   ]
        // },
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404'
    }
  ],
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

router.beforeEach((to, from, next) => {
  // when user has clicked on back button from browser or device, emit an event to close popup
  EventBus.$emit(AppConstants.BUS_EVENT_CLOSE_POPUP, true);

  if (to.path == from.path) {
    return;
  }

  if (to.path === AppConstants.NAVIGATION_PAGE_HOME) {
    // if (from.query.isApp) {
    //   router.push({ path: `${AppConstants.NAVIGATION_PAGE_INFORMATIVE}/0`, query: { isApp: true } });
    // } else {
    // }
    if (from.query.isApp && !to.query.isApp) {
      to.query['isApp'] = from.query.isApp;
    }
    router.push({ path: `${menuBuilderHelper.getInformativePageFirstItemMenu()}`, query: to.query });
    return;
  }
  // console.log(authService.isAuthenticated());
  if (authService.isAuthenticated() &&
    (to.path === AppConstants.NAVIGATION_PAGE_LOGIN ||
      to.path === AppConstants.NAVIGATION_PAGE_REGISTER ||
      to.path === AppConstants.NAVIGATION_PAGE_FORGOT_PASSWORD ||
      to.path === AppConstants.NAVIGATION_PAGE_LOST_ACCESS)) {
    // if (from.query.isApp) {
    //   router.push({ path: `${AppConstants.NAVIGATION_PAGE_INFORMATIVE}/0`, query: { isApp: true } });
    // } else {
    // }
    if (from.query.isApp && !to.query.isApp) {
      to.query['isApp'] = from.query.isApp;
    }
    router.push({ path: `${menuBuilderHelper.getInformativePageFirstItemMenu()}`, query: to.query });
    return;
  }

  // if (!authService.isAuthenticated() &&
  // (to.path === AppConstants.NAVIGATION_PAGE_ACCOUNT_CHANGE_PASSWORD ||
  // to.path === AppConstants.NAVIGATION_PAGE_ACCOUNT_NOTIFICATION ||
  // to.path === AppConstants.NAVIGATION_PAGE_ACCOUNT_SIGNATURE ||
  // to.path === AppConstants.NAVIGATION_PAGE_ACCOUNT_PRIVACY_POLICY)) {
  //   router.push({ path: `${AppConstants.NAVIGATION_PAGE_INFORMATIVE}/0` });
  // }

  // if (
  //   // authService.isAuthenticated() ||
  //   to.path === AppConstants.NAVIGATION_PAGE_HOME ||
  //   to.path === AppConstants.NAVIGATION_PAGE_LOGIN ||
  //   to.path === AppConstants.NAVIGATION_PAGE_S4LOGIN ||
  //   to.path === AppConstants.NAVIGATION_PAGE_FORGOT_PASSWORD ||
  //   to.path === AppConstants.NAVIGATION_PAGE_ERROR_404 ||
  //   to.path === AppConstants.NAVIGATION_PAGE_ERROR_500 ||
  //   to.path === AppConstants.NAVIGATION_PAGE_REGISTER ||
  //   to.path === AppConstants.NAVIGATION_PAGE_CALLBACK ||
  //   to.path === AppConstants.NAVIGATION_PAGE_COMINGSOON ||
  //   to.path === AppConstants.NAVIGATION_PAGE_RESET_PASSWORD ||
  //   to.path === AppConstants.NAVIGATION_PAGE_LOST_ACCESS ||
  //   to.path === AppConstants.NAVIGATION_PAGE_NEW_CLIENT ||
  //   to.path === AppConstants.NAVIGATION_PAGE_BROWSER_NOT_SUPPORT ||
  //   to.path === AppConstants.NAVIGATION_PAGE_MAINTENANCE
  // ) {

  //   // if (to.path === AppConstants.NAVIGATION_PAGE_HOME) {
  //   // if (authService.isAuthenticated()) {
  //   //   router.push({ path: `${AppConstants.NAVIGATION_PAGE_INFORMATIVE}/0` });
  //   //   return;
  //   // }


  //   // if (to.path === AppConstants.NAVIGATION_PAGE_LANDING_PAGE) {
  //   //   if (!authService.isAuthenticated()) {
  //   //     return next();
  //   //   }
  //   //   // router.push({ path: `${AppConstants.NAVIGATION_PAGE_INFORMATIVE}/${localStorageHelper.getCurrentUser().rights.find(r => r.habilitado).id}` });
  //   //   router.push({ path: `${AppConstants.NAVIGATION_PAGE_INFORMATIVE}/0` });
  //   //   return;
  //   // }

  //   // router.push({ path: `${AppConstants.NAVIGATION_PAGE_INFORMATIVE}/0` });
  //   // return;

  //   // return next();
  // }

  if (!authService.isAuthenticated() && to.path === AppConstants.NAVIGATION_PAGE_DOWNLOAD_PUBLIC_FILE) {
    // router.push({ path: AppConstants.NAVIGATION_PAGE_LANDING_PAGE, query: { to: to.path, token: to.query.token } });
    console.log('[redirect LOGIN]');
    if (from.query.isApp && !to.query.isApp) {
      to.query['isApp'] = from.query.isApp;
      router.push({ path: AppConstants.NAVIGATION_PAGE_LOGIN, query: { to: to.path, token: to.query.token, isApp: to.query.isApp } });
      return;
    }
    router.push({ path: AppConstants.NAVIGATION_PAGE_LOGIN, query: { to: to.path, token: to.query.token } });
    return;
  }

  // if (!authService.isAuthenticated()) {
  //   router.push({ path: `${AppConstants.NAVIGATION_PAGE_HOME}` });
  //   return next();
  // }

  // router.push({ path: AppConstants.NAVIGATION_PAGE_LANDING_PAGE, query: { to: to.path } })
  // router.push({ path: AppConstants.NAVIGATION_PAGE_LOGIN, query: { to: to.path } })
  // Specify the current path as the customState parameter, meaning it
  // will be returned to the application after auth
  // auth.login({ target: to.path });

  if (browserInfoHelper.isBrowserIE()) {
    router.push({ path: AppConstants.NAVIGATION_PAGE_BROWSER_NOT_SUPPORT })
    return next();
  }
  if (from.query.isApp && !to.query.isApp) {
    to.query['isApp'] = from.query.isApp;
    router.push({path: to.path, query: to.query })
    return next();
  }
  return next();
});


// --------- INTERCEPTORS ----------------

Axios.interceptors.request.use(
  request => {
    // add token
    // request.headers = { Authorization: 'token' };
    // add params
    // request.params = { teste: 'aaaa' };
    // console.log('interceptor request');

    const cookiesAccepted = Vue.$cookies.get('cookies_accepted');
    if (cookiesAccepted !== undefined && cookiesAccepted !== null) {
      const isAccepted = cookiesAccepted === 'true';
      request.headers['X-Cookies-Accepted'] = isAccepted  ? 'true' : 'false';
    }

    const params = request.params;

    if (store.state.correlationId) {
      request.headers['CorrelationId'] = store.state.correlationId;
    }

    if (params) {
      if (authService.isAuthenticated() && localStorageHelper.getCurrentUser()) {
        // params.append(AppConstants.PARAMS_TOKEN, localStorageHelper.getCurrentUser().token);
        // request.headers = { Authorization: `Bearer ${localStorageHelper.getCurrentUser().token}` };
        request.headers['Authorization'] = `Bearer ${localStorageHelper.getCurrentUser().token}`;
      }

      // console.log('[interceptors isDesktop]', store.state.isDesktop);
      params.append('lang', VueI18n.locale);
      // params.append('isDesktop', store.state.isDesktop);
      params.append('isDesktop', !checkDeviceTypeHelper.isNativeApp());
      request.params = params;
    }
    return request;
  },
  err => {
    console.log('error request-> ', err);
  });

let isLogoutDialogActive = false;

Axios.interceptors.response.use(
  response => {
    // console.log('interceptor response');
    return response;
  },
  error => {
    if (!error.response) {
      console.log('error response-> ', error);
      return Promise.reject(error);
    }
    if (error.response.data.errorType == 'AcceptorError') {
      // Tratamento de erro feito diretamente no catch do componente
      // messagesNotifyHelper.handleErrorSendNews(error.response.data.errors);
      return Promise.reject(error.response.data);
    }

    switch (error.response.status) {
      case 400:
        messagesNotifyHelper.handleError(error.response.data);
        break;
      case 401: // Unauthorized
        console.log('[Unauthorized][isLogoutDialogActive]', isLogoutDialogActive);
        if (authService.isAuthenticated() && !isLogoutDialogActive) {
          isLogoutDialogActive = true; 
          messageDialogHelper.displayDialogAccept(VueI18n.t('LOGOUT').Title, VueI18n.t('LOGOUT').Message, () => {
            authService.logout();
            // isLogoutDialogActive = false;
          });
          // messageDialogHelper.onDialogClose(() => {
          //   isLogoutDialogActive = false;
          // });
        }
        break;

        // if (authService.isAuthenticated()) {
        //   messageDialogHelper.displayDialogAccept(VueI18n.t('LOGOUT').Title, VueI18n.t('LOGOUT').Message, () => {
        //     authService.logout();
        //   })
        //   // if (error.response.data.message) {
        //   //   messagesNotifyHelper.warningNotify('Sessão Expirada', error.response.data.message);
        //   // } else {
        //   //   messagesNotifyHelper.warningNotify('Sessão Expirada', 'Faça o login novamente');
        //   // }
        // }
        // break;
      case 403: // Forbidden - Access Denied
        if (error.request.responseType === 'arraybuffer') {
          var decodedString = String.fromCharCode.apply(null, new Uint8Array(error.response.data));
          var message = JSON.parse(decodedString);
          // console.log('403 message obj =>', message)
          messagesNotifyHelper.handleError(message);
          return;
        }
        messagesNotifyHelper.handleError(error.response.data);
        // messagesNotifyHelper.warningNotify('403', 'Forbidden - Access Denied');
        break;
      case 503: // Internal Server Error
        messagesNotifyHelper.warningNotify(
          this.$t('TOAST_NOTIFICATIONS').internalServerErrorTitle,
          this.$t('TOAST_NOTIFICATIONS').internalServerErrorMessage
        );
        break;
      default: // Other Error

        break;
    }

    if (error.response.data) {
      return Promise.reject(error.response.data);
    }
    return Promise.reject(error);
  });

export default router
